import React, { useState, useMemo, useEffect } from 'react';
import { 
    Grid, Typography, RadioGroup, FormControlLabel, Radio, 
    FormControl, InputLabel, Select, MenuItem, OutlinedInput, 
    FormHelperText, Box, Switch, Chip, 
    TextField, DialogActions, Button,
    // 💡 ADDED IMPORTS for the Modal Structure
    Dialog, DialogTitle, DialogContent 
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

// --- DUMMY DATA (Kept the same) ---
const DUMMY_EMPLOYEES = [
    { id: 1, name: "Alice Johnson", deptId: 101, teamId: 201, desigId: 301 },
    { id: 2, name: "Bob Smith", deptId: 101, teamId: 201, desigId: 302 },
    { id: 3, name: "Charlie Brown", deptId: 102, teamId: 202, desigId: 303 },
    { id: 4, name: "Diana Prince", deptId: 102, teamId: 203, desigId: 304 },
    { id: 5, name: "Eve Adams", deptId: 103, teamId: 204, desigId: 305 },
];
const DUMMY_DEPARTMENTS = [
    { id: 101, name: "Engineering" }, { id: 102, name: "Marketing" }, { id: 103, name: "Sales" },
];
const DUMMY_TEAMS = [
    { id: 201, name: "Frontend", deptId: 101 }, { id: 202, name: "Branding", deptId: 102 }, 
    { id: 203, name: "SEO", deptId: 102 }, { id: 204, name: "North Sales", deptId: 103 },
];
const DUMMY_DESIGNATIONS = [
    { id: 301, name: "Lead Developer" }, { id: 302, name: "Junior Developer" }, 
    { id: 303, name: "Marketing Manager" }, { id: 304, name: "SEO Specialist" }, 
    { id: 305, name: "Sales Executive" },
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = { PaperProps: { style: { maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP, width: 250, }, }, };

// Helper to get employee name
const getEmployeeNameById = (id) => DUMMY_EMPLOYEES.find(e => e.id === id)?.name || `Employee ${id}`;

// 💡 Updated props signature to include 'open' and 'onClose' for self-containment
const EditGoalDetails = ({ open, onClose, initialGoalData, onSave }) => {
    
    // --- Goal Detail State ---
    const [goalTitle, setGoalTitle] = useState(initialGoalData?.title || '');
    const [goalDescription, setGoalDescription] = useState(initialGoalData?.description || '');
    
    // --- Employee Assignment State ---
    const [selectionMode, setSelectionMode] = useState("single"); 
    const [selectedEmployees, setSelectedEmployees] = useState(initialGoalData?.initialAssignedEmployees || []);
    const [isSingleSelectOpen, setIsSingleSelectOpen] = useState(false); 

    // State for 'multiple' (filter) mode
    const [selectedDepartments, setSelectedDepartments] = useState([]);
    const [selectedTeams, setSelectedTeams] = useState([]);
    const [selectedDesignations, setSelectedDesignations] = useState([]);
    const [selectAllEmployees, setSelectAllEmployees] = useState(false);

    // 💡 IMPORTANT: Reset state when the dialog is opened/closed
    useEffect(() => {
        if (open && initialGoalData) {
            setGoalTitle(initialGoalData.title || '');
            setGoalDescription(initialGoalData.description || '');
            setSelectedEmployees(initialGoalData.initialAssignedEmployees || []);
            setSelectionMode("single"); // Reset mode on open
            // Clear filters on open
            setSelectedDepartments([]);
            setSelectedTeams([]);
            setSelectedDesignations([]);
            setSelectAllEmployees(false);
        }
    }, [open, initialGoalData]);

    // --- Dynamic Filtering Logic (Memoized) ---
    const teams = useMemo(() => {
        if (selectedDepartments.length === 0) return DUMMY_TEAMS;
        return DUMMY_TEAMS.filter(team => selectedDepartments.includes(team.deptId));
    }, [selectedDepartments]);

    const designations = useMemo(() => DUMMY_DESIGNATIONS, []);

    const filteredEmployees = useMemo(() => {
        return DUMMY_EMPLOYEES.filter(employee => {
            const matchesDept = selectedDepartments.length === 0 || selectedDepartments.includes(employee.deptId);
            const matchesTeam = selectedTeams.length === 0 || selectedTeams.includes(employee.teamId);
            const matchesDesig = selectedDesignations.length === 0 || selectedDesignations.includes(employee.desigId);
            
            return matchesDept && matchesTeam && matchesDesig;
        }).sort((a, b) => a.name.localeCompare(b.name));
    }, [selectedDepartments, selectedTeams, selectedDesignations]);

    // --- Effects for State Synchronization (Refined) ---
    const handleSelectionModeChange = (event) => {
        const newMode = event.target.value;
        setSelectionMode(newMode);
        // Reset filters when changing mode
        setSelectedDepartments([]);
        setSelectedTeams([]);
        setSelectedDesignations([]);
        setSelectAllEmployees(false);
        
        // When switching back to 'single', restore initial assignments (if needed) or keep current selections
        if (newMode === 'single') {
             // For simplicity, just keep the currently selected employees. 
             // Restore logic is complex without a deep copy of initial state.
        } else {
             // Keep only valid employees when switching to 'multiple'
             setSelectedEmployees(prev => prev.filter(id => DUMMY_EMPLOYEES.some(e => e.id === id)));
        }
    };

    useEffect(() => {
        if (selectionMode === "multiple" && selectAllEmployees) {
            // Select all employees matching the current filter criteria
            setSelectedEmployees(filteredEmployees.map(e => e.id));
        }
    }, [selectAllEmployees, filteredEmployees, selectionMode]);

    // --- Handlers for User Interaction (Unchanged) ---
    const handleEmployeeSelectionChange = (event) => {
        const { value } = event.target;
        setSelectedEmployees(
            typeof value === 'string' ? value.split(',').map(Number) : value
        );
        if (selectionMode === "multiple") setSelectAllEmployees(false);
    };

    const handleEmployeeDelete = (employeeIdToDelete) => {
        setSelectedEmployees((prevSelected) =>
            prevSelected.filter((id) => id !== employeeIdToDelete)
        );
        if (selectionMode === "multiple" && selectAllEmployees) setSelectAllEmployees(false);
    };
    
    // --- Final Save Handler ---
    const handleSave = () => {
        const finalData = {
            title: goalTitle,
            description: goalDescription,
            assignedEmployeeIds: selectedEmployees,
            assignmentMode: selectionMode,
        };
        onSave(finalData); // Call the onSave handler passed from the parent
    };


    // --- THE COMPONENT RETURN: WRAPPED IN DIALOG ---
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            // Use the initial goal title for the modal title
            aria-labelledby="edit-goal-dialog-title" 
        >
            <DialogTitle id="edit-goal-dialog-title">
                <Typography variant="h5">Edit Goal: **{initialGoalData?.title || 'New Goal'}**</Typography>
            </DialogTitle>
            
            <DialogContent dividers>
                <Grid container spacing={3}>
                    {/* --- Goal Details Section --- */}
                    <Grid item xs={12}>
                        <Typography variant="h6" color="primary" gutterBottom>**Goal Details**</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Goal Title"
                                    value={goalTitle}
                                    onChange={(e) => setGoalTitle(e.target.value)}
                                    required
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Goal Description"
                                    value={goalDescription}
                                    onChange={(e) => setGoalDescription(e.target.value)}
                                    multiline
                                    rows={3}
                                />
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* --- Assignment Mode Selector --- */}
                    <Grid item xs={12}>
                        <Typography variant="h6" color="primary" sx={{ mt: 1 }}>
                            **Assign Employees**
                        </Typography>
                        <RadioGroup
                            row
                            value={selectionMode}
                            onChange={handleSelectionModeChange}
                        >
                            <FormControlLabel value="single" control={<Radio />} label="Select Employees Directly" />
                            <FormControlLabel value="multiple" control={<Radio />} label="Filter by Dept/Team/Designation" />
                        </RadioGroup>
                    </Grid>
                    
                    {/* --- Employee Chip Display (Always Visible) --- */}
                    {(selectedEmployees.length > 0) && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                **Current Assignments ({selectedEmployees.length} employees):**
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                {selectedEmployees.map((employeeId) => (
                                    <Chip
                                        key={employeeId}
                                        label={getEmployeeNameById(employeeId)}
                                        onDelete={() => handleEmployeeDelete(employeeId)}
                                        deleteIcon={<DeleteIcon />}
                                        color="primary"
                                        variant="outlined"
                                    />
                                ))}
                            </Box>
                            <hr style={{border: 'none', borderBottom: '1px dashed #ccc', marginTop: '16px'}} />
                        </Grid>
                    )}

                    {/* --- Single Employee Selection Dropdown --- */}
                    {selectionMode === "single" && (
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel id="employee-selection-label">Select Employees to Add/Remove</InputLabel>
                                <Select
                                    labelId="employee-selection-label"
                                    multiple
                                    open={isSingleSelectOpen}
                                    onOpen={() => setIsSingleSelectOpen(true)}
                                    onClose={() => setIsSingleSelectOpen(false)}
                                    value={selectedEmployees}
                                    onChange={handleEmployeeSelectionChange} 
                                    input={<OutlinedInput label="Select Employees to Add/Remove" />}
                                    renderValue={(selected) => `Selected: ${selected.length} employees`}
                                    MenuProps={MenuProps}
                                >
                                    {DUMMY_EMPLOYEES.map((employee) => (
                                        <MenuItem 
                                            key={employee.id} 
                                            value={employee.id}
                                            selected={selectedEmployees.includes(employee.id)} 
                                        >
                                            {employee.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>Add or remove employees directly from the full list.</FormHelperText>
                            </FormControl>
                        </Grid>
                    )}
                    
                    {/* --- Multiple Assignment Flow (Department/Team/Designation) --- */}
                    {selectionMode === "multiple" && (
                        <>
                            <Grid item xs={12} md={4}><FormControl fullWidth>
                                <InputLabel id="department-label">Department</InputLabel>
                                <Select labelId="department-label" multiple value={selectedDepartments} onChange={(e) => setSelectedDepartments(e.target.value)} input={<OutlinedInput label="Department" />} renderValue={(selected) => selected.map(id => DUMMY_DEPARTMENTS.find(d => d.id === id)?.name).join(', ')} MenuProps={MenuProps}>
                                    {DUMMY_DEPARTMENTS.map((dept) => (<MenuItem key={dept.id} value={dept.id} selected={selectedDepartments.includes(dept.id)}>{dept.name}</MenuItem>))}
                                </Select>
                            </FormControl></Grid>

                            <Grid item xs={12} md={4}><FormControl fullWidth disabled={selectedDepartments.length === 0}>
                                <InputLabel id="team-label">Team</InputLabel>
                                <Select labelId="team-label" multiple value={selectedTeams} onChange={(e) => setSelectedTeams(e.target.value)} input={<OutlinedInput label="Team" />} renderValue={(selected) => selected.map(id => DUMMY_TEAMS.find(t => t.id === id)?.name).join(', ')} MenuProps={MenuProps}>
                                    {teams.map((team) => (<MenuItem key={team.id} value={team.id} selected={selectedTeams.includes(team.id)}>{team.name}</MenuItem>))}
                                </Select>
                                {selectedDepartments.length === 0 && <FormHelperText>Select a Department first to filter Teams</FormHelperText>}
                            </FormControl></Grid>

                            <Grid item xs={12} md={4}><FormControl fullWidth>
                                <InputLabel id="designation-label">Designation</InputLabel>
                                <Select labelId="designation-label" multiple value={selectedDesignations} onChange={(e) => setSelectedDesignations(e.target.value)} input={<OutlinedInput label="Designation" />} renderValue={(selected) => selected.map(id => DUMMY_DESIGNATIONS.find(d => d.id === id)?.name).join(', ')} MenuProps={MenuProps}>
                                    {designations.map((d) => (<MenuItem key={d.id} value={d.id} selected={selectedDesignations.includes(d.id)}>{d.name}</MenuItem>))}
                                </Select>
                            </FormControl></Grid>

                            {/* Select All Switch and Employee Select based on filter */}
                            <Grid item xs={12} sx={{ mt: 2 }}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                                    <Typography variant="subtitle1" color="text.secondary">Available Employees to Add/Remove</Typography>
                                    <FormControlLabel
                                        control={<Switch checked={selectAllEmployees} onChange={(e) => setSelectAllEmployees(e.target.checked)} color="primary" disabled={filteredEmployees.length === 0} />}
                                        label={`Select All Filtered Employees (${filteredEmployees.length})`}
                                    />
                                </Box>
                                {!selectAllEmployees && (
                                    <FormControl fullWidth>
                                        <InputLabel id="employee-label">Select Employees</InputLabel>
                                        <Select labelId="employee-label" multiple value={selectedEmployees} onChange={handleEmployeeSelectionChange} input={<OutlinedInput label="Select Employees" />} renderValue={() => `Selected: ${selectedEmployees.length} employees`} MenuProps={MenuProps} disabled={filteredEmployees.length === 0}>
                                            {filteredEmployees.map((employee) => (
                                                <MenuItem key={employee.id} value={employee.id} selected={selectedEmployees.includes(employee.id)}>{employee.name}</MenuItem>
                                            ))}
                                        </Select>
                                        {filteredEmployees.length === 0 && (<FormHelperText error>No employees match the current filter criteria.</FormHelperText>)}
                                    </FormControl>
                                )}
                            </Grid>
                        </>
                    )}
                </Grid>
            </DialogContent>

            {/* --- Dialog Actions for Save/Cancel --- */}
            <DialogActions sx={{ width: '100%', mt: 3, p: 2, borderTop: '1px solid #eee' }}>
                <Button onClick={onClose} color="secondary" variant="outlined">
                    Cancel
                </Button>
                <Button onClick={handleSave} color="primary" variant="contained">
                    Save Changes
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditGoalDetails;